const common = {
  all: 'All',
  reportsQueue: 'Reports Queue',
  viewGrowthBasedOffNationalNorms: 'View growth and performance compared to national norms',
};

const commonErrorHelpListItems = {
  checkSpellingAndCapitalization:
    'Check the spelling and capitalization to ensure you are entering the information correctly.',
  contactSystemAdministratorIfStillUnableToAccess:
    'Contact your system administrator if you are still unable to access the web site.',
  contactSystemAdministratorForAssistance: 'Please contact your system administrator for assistance.',
  clickRefreshButton: 'Click your Refresh button or retype the web address into the address bar.',
  clickRefreshButtonWithSlowConnection:
    'Click your Refresh button or retype the web address into the address bar. If all web sites are running/loading slowly, your Internet connection may be having issues.',
  retryOrComeBackLater: 'Retry your previous activity or come back later.',
};

const reportTitle = {
  asgProjectionSummary: 'Achievement Status and Growth (ASG) Projection or Summary',
  asgQuadrant: 'Achievement Status and Growth (ASG) Summary with Quadrant Chart',
  batchServiceFamilyReport: 'Batch Print for Family Report',
  class: 'Class Report',
  classProfile: 'Class Profile',
  classBreakdown: 'Class Breakdown by RIT, Instructional Area, or Projected Proficiency',
  dataExportScheduler: 'Data Export Scheduler (.csv)',
  dataExportSchedulerBeta: 'Data Export Scheduler (.csv) - Beta',
  districtProfile: 'District Profile',
  districtSummary: 'District Summary',
  familyReport: 'Family Report',
  grade: 'Grade Report',
  gradeBreakdown: 'Grade Breakdown (.csv)',
  schoolLevelInsightsReport: 'School Profile',
  instructorsWithoutClassAssignments: 'Instructors Without Class Assignments',
  learningContinuum: 'Learning Continuum',
  potentialDuplicateProfiles: 'Potential Duplicate Profiles',
  profilesWithSharedIDs: 'Profiles With Shared IDs',
  projectedProficiencySummary: 'Projected Proficiency Summary',
  screeningAndSkillsChecklistClass: 'K–2 Screening and Skills Checklist: By Class',
  screeningAndSkillsChecklistStudent: 'K–2 Screening and Skills Checklist: By Student',
  studentGrowthSummary: 'Student Growth Summary',
  studentProfile: 'Student Profile',
  studentProgressReport: 'Student Progress',
  studentsByDistrict: 'Students by District (.csv)',
  studentsWithoutReportingAttributes: 'Students Without Reporting Attributes',
  studentsWithoutValidTestResults: 'Students Without Valid Test Results',
  testEventsByStatus: 'Test Events by Status',
  userRoles: 'User Roles',
  recoveryGoalSettingReport: 'Recovery and Goal-Setting Data File for 2020–21 School Year (.csv)',
};

const reportTypes = {
  get studentsWithoutReportingAttributes() {
    return reportTitle.studentsWithoutReportingAttributes;
  },
  get potentialDuplicateProfiles() {
    return reportTitle.potentialDuplicateProfiles;
  },
  get userRoles() {
    return reportTitle.userRoles;
  },
  get studentsWithoutValidTestResults() {
    return reportTitle.studentsWithoutValidTestResults;
  },
  get instructorsWithoutClassAssignments() {
    return reportTitle.instructorsWithoutClassAssignments;
  },
  get testEventsByStatus() {
    return reportTitle.testEventsByStatus;
  },
  studentProgress: 'Student Progress',
  classReport: 'Class Report',
  asgClassReport: 'Achievement Status and Growth',
  districtSummary: 'District Summary',
  get studentGrowthSummaryReport() {
    return reportTitle.studentGrowthSummary;
  },
  classByRIT: 'Class by RIT',
  classByGoal: 'Class by Goal',
  desCartes: 'DesCartes',
  pgid: 'PGID',
  desCartesQuery: 'DesCartes Query',
  mpgStudent: 'MPG Student',
  mpgTeacher: 'MPG Teacher',
  mpgSubSkillPerformance: 'MPG Sub-Skill Performance',
  comprehensiveDataFile: 'Comprehensive Data File',
  classByProjectedProficiency: 'Class by Projected Proficiency',
  get gradeReport() {
    return reportTitle.grade;
  },
  projectedProficiencySummary: 'Projected Proficiency Summary',
  get profilesWithSharedIDs() {
    return reportTitle.profilesWithSharedIDs;
  },
  gradeBreakdownReport: 'Grade Breakdown Report',
  studentsByDistrict: 'Students by District',
  studentProfile: 'Student Profile',
  familyReport: 'Family Report',
  get recoveryGoalSettingReport() {
    return reportTitle.recoveryGoalSettingReport;
  },
};

module.exports = {
  testEnvironments: ['local', 'development'],
  developmentEnvironments: ['local'],
  accordionTitle: {
    dataExportScheduler: 'Data Export Scheduler',
    reportLevel: 'Report Level',
    whatImDoing: "What I'm Doing",
  },
  accordionAriaLabels: {
    ReportLevelFilter: (hideShow) => {
      return `Filter by Report Level. Click this button to ${hideShow} filter options.`;
    },
    WhatImDoingFilter: (hideShow) => {
      return `Filter by What I'm Doing. Click this button to ${hideShow} filter options.`;
    },
    DataExportScheduler: (hideShow) => {
      return `Data Export Scheduler. Click this button to ${hideShow} data export links.`;
    },
    DataExportError: 'Acknowledge error message. Click this button to remove this message.',
  },
  recoveryGoalSettingReport: {
    title: 'Recovery and Goal-Setting Data File for 2020–21 School Year',
    description: `This file shows the growth needed to reach average (50th percentile growth) and stretch (60th percentile growth) goals for Spring 2021.`,
    instructions: `Select the pre-COVID-19 term you would like to compare to the Fall 2020 term.`,
    createButton: 'CREATE .CSV REPORT',
    allSchoolsCheckbox: 'All Schools',
    selectSchoolsTitle: 'Select school(s) to include in report',
    selectSchoolsDescription: 'All instructors/classes/students from selected school(s) are included in report',
    showAllSchools: 'Show all schools',
    hideSchools: 'Hide schools',
    termSelectionLabel: 'Term',
    dropDownDataMenu: {
      loadingIndicator: 'Loading...',
      errorIndicator: 'Error Loading Data',
    },
    errorBanner: {
      errorMsgBold: `There's been an error on our end. `,
      errorMsgNormal: `We're unable to create your report. `,
      linkIntro: 'Please ',
      linkText: 'refresh the page,',
      linkOutro: ' or try again later.',
    },
  },
  banner: {
    intro: 'Banner Intro Text Here',
    link: 'Banner Link Text Here',
    closeTooltip: 'Close banner',
  },
  dataExport: {
    downloadSuccessfulDataExport: (date, isComprehensive) => {
      return `Download ${date} ${isComprehensive ? 'Comprehensive' : 'Combined'} Data Export`;
    },
    noDataAvailable: 'No Data Export is available.',
    youCan: 'You can ',
    scheduleAnExport: 'schedule a Data Export.',
  },
  errors: {
    common: {
      helpList: {},
    },
    400: {
      header: 'Error 400 (Bad Request)',
      description: 'You are seeing this error message because the web address you typed is incorrect.',
      helpList: [
        commonErrorHelpListItems.checkSpellingAndCapitalization,
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    401: {
      header: 'Error 401 (Unauthorized)',
      description: 'You are seeing this error message because the username or password you entered is incorrect.',
      helpList: [
        commonErrorHelpListItems.checkSpellingAndCapitalization,
        'Check whether your caps lock key is ON, as passwords are case-sensitive.',
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    403: {
      header: 'Error 403 (Forbidden)',
      description: 'You are seeing this error message because you do not have authorization to access this page.',
      helpList: [
        commonErrorHelpListItems.checkSpellingAndCapitalization,
        'Make sure the web address points to a web page and not a directory.',
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    404: {
      header: 'Error 404 (Not Found)',
      description: 'You are seeing this error message because we could not locate the page you requested.',
      helpList: [
        commonErrorHelpListItems.checkSpellingAndCapitalization,
        commonErrorHelpListItems.clickRefreshButton,
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    405: {
      header: 'Error 405 (Method Not Allowed)',
      description: 'You are seeing this error message because your request could not be completed.',
      helpList: [commonErrorHelpListItems.contactSystemAdministratorForAssistance],
    },
    406: {
      header: 'Error 406 (Not Acceptable)',
      description: 'You are seeing this error message because your request could not be completed.',
      helpList: [commonErrorHelpListItems.contactSystemAdministratorForAssistance],
    },
    407: {
      header: 'Error 407 (Proxy Authentication Required)',
      description:
        'You are seeing this error message because the link you clicked points to a page for authorized users only.',
      helpList: [
        'Type your username/password again.',
        'If you are still unable to access your account after retrying, please contact your system administrator for assistance.',
      ],
    },
    408: {
      header: 'Error 408 (Request Timeout)',
      description: 'You are seeing this error message because your request took too long.',
      helpList: [
        commonErrorHelpListItems.clickRefreshButtonWithSlowConnection,
        'Try your request again later.',
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    409: {
      header: 'Error 409 (Conflict)',
      description:
        'You are seeing this error message because a shared document may be being edited by more than one person at the same time.',
      helpList: [
        'Please exit the system and retry your previous activity or come back later.',
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    500: {
      header: 'Error 500 (Internal Server Error)',
      description: 'You are seeing this error message because an unknown error has occurred.',
      helpList: [
        commonErrorHelpListItems.clickRefreshButtonWithSlowConnection,
        commonErrorHelpListItems.retryOrComeBackLater,
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    502: {
      header: 'Error 502 (Bad Gateway)',
      description: 'You are seeing this error message because a network error has occurred.',
      helpList: [
        commonErrorHelpListItems.clickRefreshButtonWithSlowConnection,
        commonErrorHelpListItems.retryOrComeBackLater,
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    503: {
      header: 'Error 503 (Service Unavailable)',
      description: 'You are seeing this error message because this website is currently unavailable.',
      helpList: [
        commonErrorHelpListItems.clickRefreshButtonWithSlowConnection,
        commonErrorHelpListItems.retryOrComeBackLater,
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    504: {
      header: 'Error 504 (Gateway Timeout)',
      description: 'You are seeing this error message because a server is down or not working correctly.',
      helpList: [
        commonErrorHelpListItems.clickRefreshButtonWithSlowConnection,
        commonErrorHelpListItems.retryOrComeBackLater,
        commonErrorHelpListItems.contactSystemAdministratorIfStillUnableToAccess,
      ],
    },
    1800: {
      header: 'App encountered an unexpected error',
      helpList: [
        commonErrorHelpListItems.clickRefreshButton,
        'Contact your system administrator if you are still unable to access the web site and notify them of the error in bold.',
      ],
    },
  },
  filters: {
    filterBy: 'Filter By:',
    resetFilters: 'reset filters',
  },
  growth: {
    pageTitle: 'Growth Reports Selection Page',
    noReportCards: 'Growth Reports Unavailable',
  },
  growthReportsSorting: {
    title: 'Sort By:',
    asc: 'Report Title A-Z',
    desc: 'Report Title Z-A',
    recommended: 'Recommended',
  },
  navigationTabTitle: {
    growth: 'Growth Reports',
    operational: 'Operational Reports',
    reportsQueue: common.reportsQueue,
  },
  operational: {
    pageTitle: 'Operational Reports Selection Page',
  },
  queue: {
    deletingOnDate: (date) => {
      return `(Deleting on ${date})`;
    },
    retry: 'Retry',
    download: 'Download',
    downloading: 'Downloading...',
    downloadViewPrintHeader: 'You can download, view, and print your requested reports here.',
    fetchingData: 'Fetching Data',
    noReportsInQueue: 'No reports currently in queue.',
    pageTitle: 'Reports Queue Page',
    processing: 'Processing',
    refreshingData: 'Refreshing Data...',
    readyToView: 'Ready to View',
    removeAfter14DaysHeader: 'Reports will be removed after 14 days (but the data can still be accessed).',
    removeSelected: 'Remove Selected',
    removeSelectedDisabledLabel: 'Select 1 or more reports to remove',
    removeSelectedFailurePopover: "The selected report wasn't deleted. Please try again.",
  },
  queueCheckboxAriaLabel: {
    indeterminateHeader: 'Some reports marked for deletion',
    allSelectedHeader: 'All reports marked for deletion',
    noneSelectedHeader: 'No reports marked for deletion',
    individualReport: 'Mark this report for deletion',
  },
  queueStatus: {
    expiring: 'Expiring',
    failed: 'Failed',
    noDataAvailable: 'No data available.',
    processing: 'Processing',
    readyToView: 'Ready to View',
  },
  queueTableHeader: {
    action: 'Action',
    dateCreated: ['Date', 'Created'],
    details: 'Details',
    reportId: 'Report Id',
    reportType: 'Report Type',
    status: 'Status',
  },
  pageFooter: `This system contains confidential information that may be subject to applicable federal and state law.
                 By accessing this system, you shall comply with such applicable laws, the terms and conditions of your
                 agreement with NWEA, and your entity’s policies regarding the use, transmission and handling of such
                 confidential information.`,
  reportAudienceFilters: {
    assessmentCoordinator: 'Assessment Coordinator',
    counselor: 'Counselor',
    curriculumSpecialist: 'Curriculum Specialist',
    instructionalCoach: 'Instructional Coach',
    parent: 'Parent',
    principal: 'Principal',
    student: 'Student',
    superintendent: 'Superintendent',
    teacher: 'Teacher',
  },
  disabledReportMessages: {
    recoveryGoalSettingReport: 'Recovery Goal Setting Options are Temporarily Disabled',
  },
  reportDetailsList: {
    asgProjectionSummary: [common.viewGrowthBasedOffNationalNorms, 'Compare two terms (with summary)'],
    asgQuadrant: [
      common.viewGrowthBasedOffNationalNorms,
      'Compare two terms (options to view by subject, gender, and ethnicity)',
      'Customize the data display using the interactive features of the chart',
    ],
    batchServiceFamilyReport: [
      'Generate Family Reports in bulk',
      'Use to inform conference discussions with families',
      'Print PDFs of the report to send to parents/guardians',
    ],
    batchServiceFamilyReportSpanish: [
      'Generate Family Reports in bulk',
      'Use to inform conference discussions with families',
      'Print PDFs of the report in English or Spanish to send to parents/guardians',
    ],
    class: [
      'Analyze class needs by instructional area',
      'View class performance for a term, including norm status rankings',
    ],
    classProfile: [
      'Interact with data for an entire class',
      'View student test details to determine who needs to take, retake, or complete their test',
      'Discover insights into class performance',
    ],
    classBreakdown: [
      'Use to group students with similar instructional readiness levels for a subject (by RIT) or for the instructional areas within a subject (by goal)',
      'View projected performance on state and college readiness assessments',
    ],
    dataExportScheduler: [
      'Export test results into text files',
      'Use to import data into a database, or create custom reports',
    ],
    districtProfile: [
      'Gain a holistic understanding of achievement and growth across your district.',
      'Get data insights that can support program and resource decisions. ',
    ],
    recoveryGoalSettingReport: [
      `See students’ normative achievement changes from 2019–20 school year`,
      `View growth needed to reach pre-COVID-19 achievement levels by spring`,
      `Use growth data to set average and stretch goals for spring`,
    ],
    districtSummary: [
      'See summary of RIT score test results for multiple terms',
      'Use data to inform district-level decisions and presentations',
      'View data by gender, ethnicity, and program',
    ],
    familyReport: [
      'Use to inform conference discussions with families',
      'Print a PDF of the report to send to parents/guardians',
    ],
    grade: [
      'View detailed summary test data by grade for a selected term',
      'Determine grade-level priorities',
      'Use to inform goals and adjust instruction',
    ],
    gradeBreakdown: [
      'View student achievement in subject and instructional areas',
      'Group students from across the school (no limit on the number of students)',
    ],
    schoolLevelInsights: [
      'Interactive school and grade-level data visualizations',
      'Quickly filter data based on academic year, test term, course, gender, and ethnicity',
      'Enables school leaders to identify areas of opportunity or strength',
    ],
    instructorsWithoutClassAssignments: [
      'See which instructors have no class associations for the term',
      'Use the information to then link instructors with classes',
    ],
    learningContinuum: [
      'View the skills and concepts assessed by MAP Growth',
      'Understand the relative difficulty level of test content',
      'Gather context for how a student’s RIT score aligns to the difficulty level of test content',
    ],
    potentialDuplicateProfiles: [
      'See which users may have more than one profile',
      'Use this information to merge/delete duplicate profiles',
    ],
    profilesWithSharedIDs: ['See which profiles have duplicate IDs', 'Find out which profiles are missing an ID'],
    projectedProficiencySummary: [
      'View data by grade and by school or district',
      'Use to see how students are projected to perform on state and college readiness assessments',
    ],
    screeningAndSkillsChecklistClass: [
      'View class performance from a skills checklist assessment',
      'Use to modify and tailor instruction for your entire class',
    ],
    screeningAndSkillsChecklistStudent: [
      'View individual student test results from a skills checklist assessment',
      'Use to tailor instruction for each student',
    ],
    studentGrowthSummary: ['View aggregated data by school or district', 'Compare data to norms of similar schools'],
    studentProgressReport: [
      'View a student’s overall progress from all past terms',
      'Use to discuss a student’s term-to-term growth',
    ],
    studentProfile: [
      "Get a complete picture of a student's growth and performance in one interactive report",
      'Set goals with students',
      "Gain insights into what a student's strength and opportunities are",
    ],
    studentsByDistrict: ['Verify student demographic information for a given term'],
    studentsWithoutReportingAttributes: [
      'See which students had enough information to take the test, but not enough to appear in reports',
      'See which students don’t have gender, grade, DOB, or ethnicity specified and need their profile updated',
    ],
    studentsWithoutValidTestResults: [
      'Identify the students who don’t have a valid, completed test event for a specific subject in a chosen date range',
      'Determine the progress of your test schedule and complete testing',
    ],
    testEventsByStatus: [
      'Review a summary of test events by school, grade, and subject within a specific date range',
      'See how schools are progressing through the test season and if testing is complete',
    ],
    userRoles: ['See users’ roles', 'Identify who has an active profile'],
  },
  reportGroupingTitle: {
    reportingIssuesReports: 'Reporting Issues Reports',
    testingProgressReports: 'Testing Progress Reports',
    userProfileReports: 'User Profile Reports',
  },
  reportLevelFilters: {
    all: common.all,
    class: 'Class-level',
    school: 'School-level',
    schoolOrDistrict: 'School/District-level',
    student: 'Student-level',
  },
  reportFooterLinkDisplayText: {
    asgProjectionSummary: 'Sample ASG Projection and Summary Reports',
    asgQuadrant: 'Sample ASG Summary with Quadrant Chart Report',
    class: 'Sample Class Report',
    classBreakdown: 'Sample Class Breakdown Reports',
    districtSummary: 'Sample District Summary Report',
    familyReport: 'Sample Family Report',
    familyReportVideo: 'Family Report Informational Video (4 minutes)',
    grade: 'Sample Grade Report',
    projectedProficiencySummary: 'Sample Projected Proficiency Summary Report',
    recoveryGoalSettingReport: 'Sample Recovery and Goal-Setting Data File',
    screeningAndSkillsChecklistClass: 'Sample Class K–2 Screening and Skills Checklists',
    screeningAndSkillsChecklistStudent: 'Sample Student K–2 Screening and Skills Checklists',
    studentGrowthSummary: 'Sample Student Growth Summary Report',
    studentProfile: 'Sample Student Profile Report',
    studentProgress: 'Sample Student Progress Report',
  },
  reportHeaderContent: 'Select a report to view or request. Requested reports will appear in the Reports Queue.',
  reportHeaderContentExt: 'Just finished testing? Remember: if you tested today, you can request reports tomorrow.',
  reportResourceDescription: {
    reportsVideo: 'Video tutorials that will help you interpret data and take action',
    comparativeDataToInformInstruction:
      'RIT comparison charts across grades, including college and career readiness benchmarks (2-page PDF)',
    normsAndStandardsResearchStudies: 'Detailed research briefs and the ASG and School Norms Calculators',
    nweaConnection: 'Resources, toolkits, and the ability to ask questions in our online community',
    nweaMapGrowthNormData: 'Overview with status and growth charts',
    operationalReportsReference: 'Samples and descriptions of all operational reports (8-page PDF)',
    operationalReportsSummary: 'Comparison of reports at a glance (1-page PDF)',
    quickReferenceMapReportsSummary: 'Comparison of reports at a glance (2-page PDF)',
    reportsBasic: 'Additional information about generating reports, processing time, and searching for students',
    reportsTroubelshooting: 'Answers to common questions about reports',
    mapGrowthReportsPortfolio: 'Collection of annotated sample reports',
  },
  reportResourceSectionTitle: {
    community: 'COMMUNITY',
    howDoIGetHelp: 'How do I get help?',
    howDoIUnderstandResults: 'How do I interpret the results?',
    normsAndComparativeData: 'NORMS AND COMPARATIVE DATA',
    resourcesAndGuides: 'Resources and Guides',
    whichReportsDoIWant: 'Which reports do I want?',
  },
  reportResourceLink: {
    reportsVideo: 'Reports Videos',
    comparativeDataToInformInstruction: 'Comparative Data to Inform Instruction',
    normsAndStandardsResearchStudies: 'Norms and Research Studies',
    nweaConnection: 'NWEA Connection',
    nweaMapGrowthNormData: 'NWEA MAP Growth Normative Data',
    operationalReportsReference: 'Operational Reports Reference',
    operationalReportsSummary: 'Operational Reports Summary',
    quickReferenceMapReportsSummary: 'Quick Reference\u2014MAP Reports Summary',
    reportsBasic: 'Reports Basics',
    reportsTroubelshooting: 'Reports Troubleshooting',
    mapGrowthReportsPortfolio: 'MAP Growth Reports Portfolio',
  },
  reportTitle: reportTitle,
  reportTypes: reportTypes,
  reportWhatImDoingFilters: {
    all: common.all,
    groupingStudents: 'Grouping Students',
    monitoringGrowth: 'Monitoring Growth',
    planningInstruction: 'Planning Instruction',
    talkingWithFamiliesAndStudents: 'Talking with Families and Students',
    viewingStudentTestResultDetails: 'Viewing Student Test Result Details',
  },
  skipLinks: {
    skipToReports: 'Skip to Reports',
  },
  sortByAriaLabel: (sortOrder) => {
    switch (sortOrder) {
      case 'recommended':
        return `Sort Reports Option, currently in Recommended order.`;
      case 'asc':
        return `Sort Reports Option, currently in Alphabetical order.`;
      case 'desc':
        return `Sort Reports Option, currently in Reverse Alphabetical order.`;
    }
    return `Sort Reports Option.`;
  },
  studentQuickSearch: {
    title: 'Student Quick Search',
    description: 'Search Student Profile or Student Progress Report for a single student.',
  },
};
